// eslint-disable-next-line no-restricted-imports
import Cookies from "js-cookie";
import { useWecomaIntegration } from "../gdpr/use-wecoma-integration";
import { CookieType } from "../gdpr/wecoma-models";
import { useWebStorageQueue } from "./use-web-storage-queue";
import { useWebStorageTest } from "./use-web-storage-test";
import { WebStorageJsonType, WebStorageType } from "./web-storage-type";

const { canUseStorage } = useWebStorageTest();
const { enqueue } = useWebStorageQueue();

export const useWebStorage = (storageType: WebStorageType) => {
    const { cookieTypeConsentedCached } = useWecomaIntegration();
    const storage = window[storageType];

    const canSetValue = (type: CookieType): boolean => cookieTypeConsentedCached(type);

    const setItem = (name: string, value: string, type: CookieType) => {
        if (!canSetValue(type)) {
            enqueue({ cookieType: type, itemName: name, storageType, value });
            return false;
        }

        if (canUseStorage(storageType)) {
            storage.setItem(name, value);
        } else {
            Cookies.set(name, value);
        }

        return true;
    };

    const getJson = <T = WebStorageJsonType>(name: string): T => {
        const value = canUseStorage(storageType) ? storage.getItem(name) : Cookies.get(name);
        if (!value) return null;

        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    };

    const setJson = <T = WebStorageJsonType>(name: string, value: T, type: CookieType) => {
        const str = JSON.stringify(value);
        return setItem(name, str, type);
    };

    const getItem = (name: string) => (canUseStorage(storageType) ? storage.getItem(name) : Cookies.get(name));

    const removeItem = (name: string) => (canUseStorage(storageType) ? storage.removeItem(name) : Cookies.remove(name));

    return {
        getJson,
        setJson,
        getItem,
        setItem,
        removeItem,
    };
};
